import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { ApiRoutes } from '@sb/library/api';
import { IconItem } from '@sb/library/base';
import { DisplayItemCollection, TaxDefinitionDisplayItem, TaxDefinitionSearchOptions, TaxInputItem } from '@sb/library/data-models';
import { ReplaySubject, Subject, combineLatest } from 'rxjs';
import { FormFieldBaseComponent } from '../../form-field-base.component';

@Component({
    selector: 'lib-input-tax',
    templateUrl: './tax.component.html',
    styleUrls: ['./tax.component.scss']
})
export class FormFieldTaxComponent extends FormFieldBaseComponent<TaxInputItem[], FormArray<FormControl<boolean>>> implements OnInit {

    @Input() isEditing: boolean = false;

    private _taxDefinitions: TaxDefinitionDisplayItem[] = [];
    private _visibleTaxDefinitions: TaxDefinitionDisplayItem[] = [];
    private _loadingTaxes: boolean = true;
    private _indexMap: Record<number, TaxDefinitionDisplayItem> = {};
    private _taxDefinitionsReady = new ReplaySubject<void>(1);
    private _currentValueReady = new Subject<void>();
    private _currentValue!: TaxInputItem[];
    private _autofilledByTaxDefinitionID!: Record<string, boolean>;

    constructor() {
        super();
        this.viewControl = new FormArray<FormControl<boolean>>([]);

        combineLatest([this._taxDefinitionsReady, this._currentValueReady]).subscribe(_ => {
            this._loadingTaxes = false;
            this.UpdateUi(this._currentValue);
        });
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this._loadingTaxes = true;

        ApiRoutes.TaxDefinitions.Search.Call({
            Body: new TaxDefinitionSearchOptions()
        }).subscribe((taxDefinitions: DisplayItemCollection<TaxDefinitionDisplayItem>) => {
            this._taxDefinitions = taxDefinitions.ItemSubset;
            this._taxDefinitionsReady.next();
        });
    }

    private GetResultItem(value: TaxDefinitionDisplayItem[]): TaxInputItem[] {
        return value.map(taxDefinition => new TaxInputItem({
            TaxDefinitionID: taxDefinition.ID,
            RateOverride: taxDefinition.PercentageRate,
            Autofilled: this._autofilledByTaxDefinitionID[taxDefinition.ID!]
        }));
    }

    override writeValue(value: TaxInputItem[]): void {
        if (value) {
            this._currentValue = value;
            this._currentValueReady.next();
        }
    }

    private UpdateUi(value: TaxInputItem[]) {
        let existingTaxDefinitionIDs: string[] = [];

        existingTaxDefinitionIDs = value.map(t => t.TaxDefinitionID!);

        this._visibleTaxDefinitions = this._taxDefinitions.filter(tax => existingTaxDefinitionIDs.includes(tax.ID!) || tax.Active).orderByKey('Name');
        this.viewControl = new FormArray<FormControl<boolean>>([]);
        this._autofilledByTaxDefinitionID = {};

        this._visibleTaxDefinitions.forEach((taxDefinition, index) => {
            const isSelected = this.isEditing ? existingTaxDefinitionIDs.includes(taxDefinition.ID!) : taxDefinition.IsChargedByDefault;
            this.viewControl.Add(isSelected);
            this._indexMap[index] = taxDefinition;
            const previouslySelectedTax = value.find(t => t.TaxDefinitionID === taxDefinition.ID);
            taxDefinition.PercentageRate = previouslySelectedTax ? previouslySelectedTax.RateOverride! : taxDefinition.PercentageRate;
            this._autofilledByTaxDefinitionID[taxDefinition.ID!] = previouslySelectedTax ? previouslySelectedTax.Autofilled : false;
        });

        this.ChangeEvent(true);
    }

    override ObservePristine(): void {
        // This is done below in ChangeEvent instead
    }

    ChangeEvent(manualUpdate: boolean = false) {
        let result: TaxDefinitionDisplayItem[] = [];

        this.viewControl.controls.forEach((control, valueIndex) => {
            if (control.value) {
                result.push(this._indexMap[valueIndex]);
            } else {
                const tax = this.formControl.value.find(tax => tax.TaxDefinitionID === this._indexMap[valueIndex].ID);
                if (tax) {
                    this._autofilledByTaxDefinitionID[tax.TaxDefinitionID!] = false;
                }
            }
        });

        this.formControl.setValue(this.GetResultItem(result), {emitModelToViewChange: false});
        this.SetFormControlValue(this.GetResultItem(result));
        
        if (!manualUpdate) {
            this.formControl.isDirty = true;
        }
        
        super.OnBlur();
    }

    get taxDefinitions(): TaxDefinitionDisplayItem[] {
        return this._taxDefinitions;
    }

    get visibleTaxDefinitions(): TaxDefinitionDisplayItem[] {
        return this._visibleTaxDefinitions;
    }

    get autofilledByTaxDefinitionID(): Record<string, boolean> {
        return this._autofilledByTaxDefinitionID;
    }

    get isLoadingTaxes(): boolean {
        return this._loadingTaxes;
    }
}
